import { type Dispatch } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { getManagementToken } from '@/lib/storage/local-storage'
import { buildShareUrl, getShareExtension } from '@/lib/utils/share-url'
import { getStoredApiKey } from '@/lib/account/auth'
import {
  loadShareForViewing,
  validateShareAccess,
} from '@/lib/shares/load'
import { getShareMeta, getShareBlob } from '@/lib/api/shares'
import { decryptShareBlob } from '@/lib/crypto'
import type { EditorState, ViewingShareMeta } from '../state'
import type { EditorAction } from '../state'
import type { ShareFormat } from '@/types/share'

interface UseShareViewingOptions {
  saveCurrentDraftNow: () => void
  handleContentLoaded: (content: string, filename?: string, format?: ShareFormat) => void
}

/**
 * Handles viewing and cloning existing shares.
 * - handleSelectFile: fetch + decrypt + load a share in-place
 * - handleCloneShare: fetch + decrypt + create a new draft from a share
 */
export function useShareViewing(
  _state: EditorState,
  dispatch: Dispatch<EditorAction>,
  options: UseShareViewingOptions,
) {
  const navigate = useNavigate()
  const { saveCurrentDraftNow, handleContentLoaded } = options

  const handleSelectFile = async (shareId: string) => {
    saveCurrentDraftNow()

    const apiKey = getStoredApiKey()
    if (!apiKey) {
      toast.error('Please create an anonymous account to view your shares')
      return
    }

    dispatch({ type: 'SET_LOADING', operation: 'selectFile' })
    dispatch({ type: 'SET_SELECTED_FILE_ID', fileId: shareId })

    try {
      const result = await loadShareForViewing(shareId, apiKey)
      if (result === null) {
        // Can't decrypt with API key, redirect to passcode view
        const meta = await getShareMeta(shareId, { apiKey })
        navigate(buildShareUrl(shareId, getShareExtension(meta.filename)))
        return
      }

      const managementToken = result.meta.isModifiable ? getManagementToken(shareId) : undefined
      const shareMeta: ViewingShareMeta = {
        isModifiable: result.meta.isModifiable,
        managementToken: managementToken ?? undefined,
      }

      dispatch({
        type: 'LOAD_SHARE',
        shareId,
        content: result.content,
        filename: result.filename,
        meta: shareMeta,
        fileType: result.fileType,
      })
    } catch (error) {
      console.error('Failed to load share:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load share')
      dispatch({ type: 'SET_SELECTED_FILE_ID', fileId: undefined })
    } finally {
      dispatch({ type: 'SET_LOADING', operation: null })
    }
  }

  const handleCloneShare = async (shareId: string) => {
    const apiKey = getStoredApiKey()
    if (!apiKey) {
      toast.error('Please create an anonymous account to clone shares')
      return
    }

    dispatch({ type: 'SET_LOADING', operation: 'cloneShare' })
    try {
      const meta = await getShareMeta(shareId, { apiKey })

      const access = validateShareAccess(meta)
      if (!access.valid) {
        toast.error(
          access.reason === 'expired'
            ? 'This share has expired'
            : 'This share has been consumed',
        )
        return
      }

      const blob = await getShareBlob(shareId)

      const decryptedContent = await decryptShareBlob(blob, meta, apiKey)
      if (decryptedContent === null) {
        toast.error('Cannot clone: unable to decrypt share')
        return
      }

      handleContentLoaded(decryptedContent, meta.filename || 'untitled', meta.format)
      toast.success('Share cloned as new draft')
    } catch (error) {
      console.error('Failed to clone share:', error)
      toast.error('Failed to clone share')
    } finally {
      dispatch({ type: 'SET_LOADING', operation: null })
    }
  }

  return { handleSelectFile, handleCloneShare }
}
